import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import "./SignUp.css";

const SignUp = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
    domain1: "",
    domain2: "",
    domain3: "",
    cv: null,
  });
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/reviewer/get-domain`
        );
        setDomains(response.data.map((domain) => domain.mainDomain));
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error fetching domains",
          text: "Unable to retrieve domains from the server",
        });
      }
    };

    fetchDomains();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, cv: e.target.files[0] });
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Passwords do not match",
      });
      setIsLoading(false);
      return;
    }

    if (
      formData.role === "reviewer" &&
      !(formData.domain1 || formData.domain2 || formData.domain3)
    ) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Please select at least one domain.",
      });
      setIsLoading(false);
      return;
    }

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach((key) => {
        formDataToSend.append(key, formData[key]);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/new-sign-up/sign-up`,
        formDataToSend
      );

      if (
        response.status === 400 &&
        response.data.message === "Email already used with this role"
      ) {
        Swal.fire({
          icon: "error",
          title: "Signup Failed",
          text: "This email is already registered with the selected role.",
        });
        setIsLoading(false);
        return;
      }

      if (response.status === 200 && response.data.token) {
        setStep(2);
        localStorage.setItem("signupToken", response.data.token);

        Swal.fire({
          icon: "success",
          title: "Complete your Signup by OTP",
          text: "Please check your email for the OTP",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Signup Failed",
          text: response.data.message || "Failed to sign up",
        });
      }
    } catch (error) {
      console.error("Error signing up:", error);
      Swal.fire({
        icon: "error",
        title: "Signup Error",
        text:
          error.response?.data?.message ||
          "Something went wrong. Please try again later.",
      });
    }
    setIsLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem("signupToken");

      if (!token) {
        Swal.fire({
          icon: "error",
          title: "Verification Error",
          text: "No token found. Please try signing up again.",
        });
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/new-sign-up/verify-otp`,
        { otp, token }
      );

      if (response.status === 200) {
        const user = response.data.user;

        if (user.role === "reviewer" && !user.isApproved) {
          Swal.fire({
            icon: "info",
            title: "Approval Pending",
            text: "Your account is pending approval by an admin.",
          });
          navigate("/");
        } else if (user.role === "author" && user.isApproved) {
          Swal.fire({
            icon: "success",
            title: "Complete your Registration",
            text: "You can login your account.",
          });
          navigate("/login");
        } else {
          Swal.fire({
            icon: "success",
            title: "OTP Verified",
            text: "Your account has been successfully verified.",
          }).then(() => {
            localStorage.removeItem("signupToken");
            navigate("/thank-you");
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text:
            response.data.message || "Failed to verify OTP. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      Swal.fire({
        icon: "error",
        title: "Verification Error",
        text:
          error.response?.data?.message ||
          "Something went wrong. Please try again later.",
      });
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-card">
        <div className="signup-header">
          <h1>Create Account</h1>
          <p>Join our community today!</p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleSignup} className="signup-form">
            <div className="name-fields">
              <div className="input-group">
                <PersonOutlineIcon className="input-icon" />
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="input-group">
                <PersonOutlineIcon className="input-icon" />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <EmailOutlinedIcon className="input-icon" />
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="input-group">
              <LockOpenOutlinedIcon className="input-icon" />
              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </div>

            <div className="input-group">
              <LockOpenOutlinedIcon className="input-icon" />
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>

            <div className="input-group">
              <AssignmentIndOutlinedIcon className="input-icon" />
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select Role
                </option>
                <option value="reviewer">Reviewer</option>
                <option value="author">Author</option>
              </select>
            </div>

            {formData.role === "reviewer" && (
              <>
                <div className="expertise-section">
                  <h3>Expertise Fields</h3>
                  <div className="expertise-fields">
                    <div className="input-group">
                      <CategoryOutlinedIcon className="input-icon" />
                      <select
                        name="domain1"
                        value={formData.domain1}
                        onChange={handleChange}
                      >
                        <option value="">Select Domain 1</option>
                        {domains.map((domain, index) => (
                          <option key={index} value={domain}>
                            {domain}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="input-group">
                      <CategoryOutlinedIcon className="input-icon" />
                      <select
                        name="domain2"
                        value={formData.domain2}
                        onChange={handleChange}
                      >
                        <option value="">Select Domain 2</option>
                        {domains.map((domain, index) => (
                          <option key={index} value={domain}>
                            {domain}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="input-group">
                      <CategoryOutlinedIcon className="input-icon" />
                      <select
                        name="domain3"
                        value={formData.domain3}
                        onChange={handleChange}
                      >
                        <option value="">Select Domain 3</option>
                        {domains.map((domain, index) => (
                          <option key={index} value={domain}>
                            {domain}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="file-upload-group">
                  <label className="file-upload-label">
                    <CloudUploadOutlinedIcon className="upload-icon" />
                    <span>
                      {formData.cv ? formData.cv.name : "Upload CV (PDF)"}
                    </span>
                    <input
                      type="file"
                      name="cv"
                      accept=".pdf"
                      className="file-input"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </>
            )}

            <button
              type="submit"
              className="signup-button"
              disabled={isLoading}
            >
              {isLoading ? "Creating Account..." : "Create Account"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="otp-form">
            <h3>Verify Email</h3>
            <p className="otp-instruction">
              Check your email for the verification code
            </p>
            <div className="input-group">
              <LockOpenOutlinedIcon className="input-icon" />
              <input
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="verify-button">
              Verify OTP
            </button>
          </form>
        )}

        <div className="signup-footer">
          <p>
            Already have an account? <a href="/login">Sign In</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
